import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Edit2, Pause, Play, Trash2, Plus } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { getServiceArea, getStatus, APPROVAL_PATHS, isActiveProfile } from './constants'
import { format, addDays, addMonths } from 'date-fns'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const ORDINAL = n => n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : n === 31 ? 'Last day' : `${n}th`

function getScheduleLabel(task) {
  if (task.recurrence_type === 'weekly') return `Every ${DAYS[task.recurrence_day_of_week] || 'Monday'}`
  if (task.recurrence_type === 'monthly') return `Monthly on the ${ORDINAL(task.recurrence_day_of_month)}`
  if (task.recurrence_type === 'custom') return `Every ${task.recurrence_interval_days} days`
  return '—'
}

function getNextDue(task) {
  const now = new Date()
  if (task.recurrence_type === 'weekly') {
    const day = task.recurrence_day_of_week ?? 1
    const diff = (day - now.getDay() + 7) % 7 || 7
    return addDays(now, diff)
  }
  if (task.recurrence_type === 'monthly') {
    const next = addMonths(now, 1)
    const day = task.recurrence_day_of_month === 31
      ? new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate()
      : task.recurrence_day_of_month
    return new Date(next.getFullYear(), next.getMonth(), day)
  }
  if (task.recurrence_type === 'custom') {
    return addDays(now, task.recurrence_interval_days || 7)
  }
  return null
}

// ─── Edit modal ───────────────────────────────────────────────────────────────
function EditRecurringModal({ task, profiles, clients, onClose, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState({
    title: task.title,
    recurrence_type: task.recurrence_type || 'weekly',
    recurrence_day_of_week: task.recurrence_day_of_week ?? 1,
    recurrence_day_of_month: task.recurrence_day_of_month ?? 1,
    recurrence_interval_days: task.recurrence_interval_days ?? 7,
    assigned_to: task.assigned_to || '',
    service_area: task.service_area || 'website',
    approval_path: task.approval_path || 'internal',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    const { error } = await supabase.from('tasks').update(form).eq('id', task.id)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    toast('Recurring task updated', 'success')
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">Edit Recurring Task</span>
          <button className="btn-icon" onClick={onClose}><span style={{ fontSize: 16 }}>×</span></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Task Title</label>
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Service Area</label>
              <select className="form-control" value={form.service_area} onChange={e => set('service_area', e.target.value)}>
                {['ads','seo','website','social','email','design','copywriting','account_management','other'].map(s => (
                  <option key={s} value={s}>{s.replace('_',' ')}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Recurrence</label>
            <select className="form-control" value={form.recurrence_type} onChange={e => set('recurrence_type', e.target.value)}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom interval</option>
            </select>
          </div>
          {form.recurrence_type === 'weekly' && (
            <div className="form-group">
              <label className="form-label">Day of Week</label>
              <select className="form-control" value={form.recurrence_day_of_week} onChange={e => set('recurrence_day_of_week', parseInt(e.target.value))}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          )}
          {form.recurrence_type === 'monthly' && (
            <div className="form-group">
              <label className="form-label">Day of Month</label>
              <select className="form-control" value={form.recurrence_day_of_month} onChange={e => set('recurrence_day_of_month', parseInt(e.target.value))}>
                {Array.from({length: 28}, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{ORDINAL(d)}</option>
                ))}
                <option value={31}>Last day of month</option>
              </select>
            </div>
          )}
          {form.recurrence_type === 'custom' && (
            <div className="form-group">
              <label className="form-label">Every how many days?</label>
              <input className="form-control" type="number" min="1" value={form.recurrence_interval_days} onChange={e => set('recurrence_interval_days', parseInt(e.target.value))} />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Approval Path</label>
            <select className="form-control" value={form.approval_path} onChange={e => set('approval_path', e.target.value)}>
              {APPROVAL_PATHS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────
export default function RecurringTasksView({ clients, profiles, currentUser, onNewTask }) {
  const toast = useToast()
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterClient, setFilterClient] = useState('')
  const [editingTask, setEditingTask] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('tasks')
      .select('*, client:clients(name), assignee:profiles!tasks_assigned_to_fkey(full_name)')
      .eq('is_recurring', true)
      .is('recurrence_parent_id', null) // only show originals, not spawned copies
      .order('created_at', { ascending: false })
    if (filterClient) query = query.eq('client_id', filterClient)
    const { data } = await query
    setTasks(data || [])
    setLoading(false)
  }, [filterClient])

  useEffect(() => { load() }, [load])

  const togglePause = async (task) => {
    // Pausing = mark as not_recurring temporarily by setting is_recurring false
    // We use a simple convention: paused tasks have is_recurring = false
    const { error } = await supabase.from('tasks').update({ is_recurring: !task.is_recurring }).eq('id', task.id)
    if (error) { toast('Error', 'error'); return }
    toast(task.is_recurring ? 'Recurring task paused' : 'Recurring task resumed', 'success')
    load()
  }

  const deleteRecurring = async (task) => {
    if (!window.confirm(`Stop "${task.title}" from recurring? This won't delete past instances.`)) return
    const { error } = await supabase.from('tasks').update({ is_recurring: false, recurrence_type: null }).eq('id', task.id)
    if (error) { toast('Error', 'error'); return }
    toast('Recurring task stopped', 'success')
    load()
  }

  return (
    <div>
      <div className="filters-bar" style={{ marginBottom: 20 }}>
        <select className="filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-gold" onClick={onNewTask}>
            <Plus size={15} /> New Recurring Task
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <RefreshCw size={48} className="empty-state-icon" />
          <h3>No recurring tasks yet</h3>
          <p>Create a task and check "Recurring Task" to set up a schedule</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="task-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Task</th>
                <th>Client</th>
                <th>Assigned To</th>
                <th>Schedule</th>
                <th>Next Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => {
                const sa = getServiceArea(task.service_area)
                const st = getStatus(task.status)
                const nextDue = getNextDue(task)
                const isPaused = !task.is_recurring

                return (
                  <tr key={task.id} style={{ opacity: isPaused ? 0.6 : 1 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <RefreshCw size={13} style={{ color: 'var(--teal)', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13.5 }}>{task.title}</div>
                          {sa && <span className="badge" style={{ background: sa.color + '18', color: sa.color, fontSize: 11, marginTop: 2 }}>
                            <span className="service-dot" style={{ background: sa.color }} />{sa.label}
                          </span>}
                        </div>
                      </div>
                    </td>
                    <td><span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{task.client?.name || '—'}</span></td>
                    <td><span style={{ fontSize: 13 }}>{task.assignee?.full_name || <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}</span></td>
                    <td>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy-mid)' }}>
                        {isPaused ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Paused</span> : getScheduleLabel(task)}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {isPaused ? '—' : nextDue ? format(nextDue, 'MMM d, yyyy') : '—'}
                      </span>
                    </td>
                    <td>
                      {st && <span className="status-pill" style={{ background: st.bg, color: st.color }}>
                        {st.icon} {st.label}
                      </span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-icon" title="Edit" onClick={() => setEditingTask(task)}>
                          <Edit2 size={13} />
                        </button>
                        <button className="btn-icon" title={isPaused ? 'Resume' : 'Pause'} onClick={() => togglePause(task)}>
                          {isPaused ? <Play size={13} /> : <Pause size={13} />}
                        </button>
                        <button className="btn-icon" title="Stop recurring" onClick={() => deleteRecurring(task)}
                          style={{ color: 'var(--red)' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingTask && (
        <EditRecurringModal
          task={editingTask}
          profiles={profiles}
          clients={clients}
          onClose={() => setEditingTask(null)}
          onSaved={() => { setEditingTask(null); load() }}
        />
      )}
    </div>
  )
}
